// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomProductContextAvailability from "@tps/App/Components/Product/CustomProductContextAvailability";
import ProductEmployeePartNumbers from "@tps/App/Components/Product/ProductEmployeePartNumbers";
import ProductShippingAndActions, {
    ProductShippingAndActionsStyle,
} from "@tps/App/Components/Product/ProductShippingAndActions";
import { CustomProductContext, CustomProductContextModel } from "@tps/App/Store/Contexts/CustomProductContext";
import React, { ReactNode, useState } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isAuthenticated: state.context.session.isAuthenticated && !state.context.session.isGuest,
});

interface OwnProps {
    product: ProductModel;
    productInfo: ProductInfo | undefined;
    inquireMessage: ReactNode;
    extendedStyles?: ProductDetailsProductCardStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsProductCardStyles {
    topSection?: InjectableCss;
    bottomSection?: InjectableCss;
    descriptionDocumnetsCountryWrapper?: InjectableCss;
    descriptionDocumnetsWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    description?: TypographyProps;
    country?: TypographyProps;
    documents?: InjectableCss;
    documentLink?: LinkPresentationProps;
    productPriceStyles?: ProductPriceStyles;
    productNumber?: TypographyProps;
    descriptionProductNumberWrapper?: InjectableCss;
    productShippingAndActionsStyles?: ProductShippingAndActionsStyle;
}

export const productDetailsProductCardStyles: ProductDetailsProductCardStyles = {};

const ProductDetailsProductCard = ({
    product,
    extendedStyles,
    productInfo,
    isAuthenticated,
    inquireMessage,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productDetailsProductCardStyles, extendedStyles));

    const [qty, setQty] = useState(productInfo?.qtyOrdered || 1);
    const [unitOfMeasure, setUnitOfMeasure] = useState(productInfo?.unitOfMeasure || "");
    const [warehouseId, setWarehouseId] = useState("");
    const isSpecialOrder = product?.detail?.isSpecialOrder;

    const updateQuantity = (qtyOrdered: number) => {
        setQty(qtyOrdered);
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        setUnitOfMeasure(unitOfMeasure);
    };

    const updateWarehouseId = (warehouseId: string) => {
        setWarehouseId(warehouseId);
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product: {
            ...product,
            properties: {
                warehouseId,
            },
        },
        productInfo: {
            ...productInfo,
            qtyOrdered: qty,
            unitOfMeasure,
        },
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
        onWarehouseChanged: updateWarehouseId,
    } as CustomProductContextModel;

    return (
        <>
            <CustomProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.topSection}>
                    <StyledWrapper {...styles.descriptionProductNumberWrapper}>
                        <StyledWrapper {...styles.descriptionDocumnetsWrapper}>
                            <Typography {...styles.description}>
                                <strong>{product.properties?.description}</strong>
                                {product.properties?.title}
                            </Typography>
                            {product.documents && (
                                <StyledWrapper {...styles.documents}>
                                    {product.documents.map(document => (
                                        <Link
                                            key={document.id.toString()}
                                            href={document.filePath}
                                            target="_new"
                                            {...styles.documentLink}
                                            data-test-selector={`productDetails_${document.id}`}
                                        >
                                            {document.name}
                                        </Link>
                                    ))}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                        <ProductEmployeePartNumbers product={product} />
                    </StyledWrapper>
                    {isAuthenticated && !isSpecialOrder && (
                        <StyledWrapper {...styles.qtyAndPriceWrapper}>
                            <ProductPrice
                                showLabel={false}
                                product={productContext}
                                extendedStyles={styles.productPriceStyles}
                            />
                            <CustomProductContextAvailability isProductDetailsPage={true} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.bottomSection}>
                    {isAuthenticated && isSpecialOrder ? (
                        <Typography>{inquireMessage}</Typography>
                    ) : (
                        <ProductShippingAndActions
                            showNumberInCart={true}
                            showSpeedAbilityIcon={true}
                            extendedStyles={styles.productShippingAndActionsStyles}
                        />
                    )}
                </StyledWrapper>
            </CustomProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps)(ProductDetailsProductCard);
